import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { Checkbox, Form, Upload, Image, Input } from 'antd';
import LoadableButton from "../../../../../components/buttons/LoadableButton.jsx";
import { DepositAmountApi, getBankDataApi } from '../../../../../api/request/wallet/cashdeposit.js';
import { handleApiError } from '../../../../../utils/handleApiError.js';
import { getDepositAmountError } from '../../../../../utils/validateDepositAmount.js';
import ActiveBankDetails from './ActiveBankDetails.jsx';

const CashDeposit = ({ amount, loginTerminalId, resetAll, validateAmount, tncFileUrl, paymentMethod }) => {
    const [form] = Form.useForm();
    const { userDetails } = useSelector(state => state.user);
    const [isLoading, setIsLoading] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [bankData, setBankData] = useState([]);
    const [signatureFileList, setSignatureFileList] = useState([]);
    const [proofFileList, setProofFileList] = useState([]);
    const needsProofOfFund = Number(amount) > 1000;
    // tncFileUrl is passed as a prop

    const getBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
    });

    const extractOriginFileObjs = (fileArray) => {
        return fileArray ? fileArray.map((file) => file.originFileObj) : [];
    };

    const handleSubmit = async (values) => {
        setIsLoading(true);
        try {
            const amountError = validateAmount || getDepositAmountError(amount, paymentMethod);
            if (amountError) {
                toast.error(amountError);
                return;
            }
            const UploadDocument = extractOriginFileObjs(values?.UploadDocument);
            const proofOfFund = extractOriginFileObjs(values?.ProofOfFund);
            if (needsProofOfFund && proofOfFund.length === 0) {
                toast.error('Please upload Source of fund.');
                return;
            }
            const isWallet = loginTerminalId === 'wallet';
            let formData = new FormData();
            formData.append("paymentgatewayId", paymentMethod?.id);
            formData.append("UserId", userDetails?._id);
            formData.append("ReferenceId", values?.ReferenceId);
            formData.append("Comment", values?.Comment || "");
            formData.append("Amount", Number(amount).toFixed(2));
            formData.append("DepositType", 3);
            formData.append("loginTerminalId", isWallet ? 0 : loginTerminalId);
            UploadDocument.forEach((file) => formData.append("PaymentProof", file));
            if (needsProofOfFund && proofOfFund[0]) formData.append("ProofOfFund", proofOfFund[0]);

            let apiHeaders = { headers: { Accept: "application/json", "Content-Type": "multipart/form-data" } };

            const { data } = await DepositAmountApi(formData, apiHeaders);
            if (data?.success) {
                toast.success(data?.message);
                setSignatureFileList([]);
                setProofFileList([]);
                form.resetFields();
                resetAll();
            }
        } catch (error) {
            handleApiError(error);
        } finally {
            setIsLoading(false);
        }
    }

    const handlePreview = async (file) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj);
        }
        setPreviewImage(file.url || file.preview);
        setPreviewOpen(true);
    };

    const handleSignatureChange = ({ fileList: newSignatureList }) => {
        setSignatureFileList(newSignatureList);
        form.setFieldsValue({ UploadDocument: newSignatureList });
    };

    const handleProofChange = ({ fileList: newProofList }) => {
        const nextList = newProofList.slice(-1);
        setProofFileList(nextList);
        form.setFieldsValue({ ProofOfFund: nextList });
    };

    useEffect(() => {
        if (!needsProofOfFund) {
            setProofFileList([]);
            form.setFieldsValue({ ProofOfFund: [] });
        }
    }, [needsProofOfFund, form]);

    const UploadButton = (
        <button type="button">
            <div className='flex items-center justify-center gap-2 border rounded-md py-1.5 px-4 text-lg text-gray-500 font-medium'>
                <img src="/assets/icons/Upload_Document_icon.svg" alt="icon" className='h-5' />
                <span>Click to Upload</span>
            </div>
        </button>
    );

    const getBankDetails = async () => {
        try {
            const { data } = await getBankDataApi()
            setBankData(Array.isArray(data?.data) ? data.data : []);
        } catch (err) { }
    }

    useEffect(() => {
        getBankDetails()
    }, [])


    return (
        <div>
            <Form form={form} layout="vertical" className='space-y-5' onFinish={handleSubmit}>
                <ActiveBankDetails banks={bankData} />
                <hr />

                <div className="grid md:grid-cols-2 gap-5">
                    <Form.Item
                        name="ReferenceId"
                        label={<span className="text-lg text-gray-500 font-medium">Reference Id</span>}
                        rules={[{ required: true, message: 'Please enter reference id.' }]}
                    >
                        <Input placeholder="Reference Id" size="large" className="py-3 md:py-1.5" />
                    </Form.Item>
                </div>

                <Form.Item
                    name="UploadDocument"
                    label={<span className="text-lg text-gray-500 font-medium">Upload Document</span>}
                    rules={[{ required: true, message: 'Please Upload Document.' }]}
                >
                    <Upload
                        beforeUpload={() => false}
                        listType="picture"
                        className="mt-1"
                        fileList={signatureFileList}
                        onPreview={handlePreview}
                        onChange={handleSignatureChange}
                    >
                        {signatureFileList.length >= 2 ? null : UploadButton}
                    </Upload>
                </Form.Item>

                {needsProofOfFund && (
                    <Form.Item
                        name="ProofOfFund"
                        label={<span className="text-lg text-gray-500 font-medium">Source of fund</span>}
                        rules={[{ required: true, message: 'Please upload Source of fund.' }]}
                    >
                        <Upload
                            beforeUpload={() => false}
                            listType="picture"
                            className="mt-1"
                            fileList={proofFileList}
                            onPreview={handlePreview}
                            onChange={handleProofChange}
                            maxCount={1}
                        >
                            {proofFileList.length >= 1 ? null : UploadButton}
                        </Upload>
                    </Form.Item>
                )}

                {previewImage && (
                    <Image
                        src={previewImage}
                        wrapperStyle={{ display: 'none' }}
                        preview={{
                            visible: previewOpen,
                            onVisibleChange: (visible) => setPreviewOpen(visible),
                            afterOpenChange: (visible) => !visible && setPreviewImage(''),
                        }}
                    />
                )}

                <Form.Item
                    name='conditions'
                    valuePropName="checked"
                    rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error('Please accept the terms and conditions.')) }]}
                >
                    <Checkbox className='mt-4'>
                        Yes, I agreed to the{' '}
                        {tncFileUrl && (
                            <a href={tncFileUrl} target="_blank" rel="noopener noreferrer" className="primary underline hover:underline duration-300">Terms & Conditions</a>
                        )}
                    </Checkbox>
                </Form.Item>

                <LoadableButton
                    className='bg-primary text-sm text-white font-semibold rounded-lg px-12 py-2 uppercase themeHover duration-500'
                    type="Submit"
                    lable='Submit'
                    isLoading={isLoading}
                    loadingLable='Adding request...'
                />
            </Form>
        </div>
    )
}

export default CashDeposit
